import { BARS_RANGE, MAX_TRACKS } from '@/utils/constants';
import { defaultTrackEffects } from '@/state/trackModel';
import { classSongMap, selectClassSongMapId } from '@/state/classSteps';

export const CLASS_GROUPS = ['melody', 'drums', 'texture', 'fx'];

export const CLASS_GROUP_META = {
  melody: { label: 'Melodies', color: 'var(--ba-class-melodies)' },
  drums: { label: 'Drums', color: 'var(--ba-class-drums)' },
  texture: { label: 'Textures', color: 'var(--ba-class-textures)' },
  fx: { label: 'Sound effects', color: 'var(--ba-class-sound-effects)' },
};

export const classMapLength = (bpm) => (Number(bpm) >= 120 ? 80 : 40);

const trackId = (group, index, loopId) => `class-track-${group}-${index}-${loopId}`;
const clipId = (group, index, loopId) => `class-clip-${group}-${index}-${loopId}`;

export function buildClassSession(pack) {
  const bpm = Number(pack.bpm);
  const mapId = selectClassSongMapId(bpm);
  const tracks = CLASS_GROUPS.flatMap((group) => {
    const loops = Array.isArray(pack.slots?.[group]) ? pack.slots[group] : [];
    return loops.filter((loop) => loop && typeof loop.bars === 'number' && Number.isFinite(loop.bars) && loop.bars > 0).map((loop, index) => {
      const color = CLASS_GROUP_META[group].color;
      const bars = Number(loop.bars);
      return {
        id: trackId(group, index, loop.id),
        name: loop.name,
        instrument: group,
        color,
        volume: 80,
        pan: 0,
        muted: true,
        solo: false,
        effects: defaultTrackEffects(),
        classSlot: { group, index },
        inBank: false,
        clips: [{
          id: clipId(group, index, loop.id),
          loop_id: loop.id,
          name: loop.name,
          color,
          start_bar: 0,
          bars,
          offset: 0,
          sourceBpm: Number(loop.bpm),
          naturalLoopBars: bars,
          isWholeLoop: true,
          fade_in: 0,
          fade_out: 0,
          audio_url: null,
          key: loop.key || null,
        }],
      };
    });
  });
  return {
    tracks,
    sectionMarkers: [],
    classSession: {
      packId: pack.id,
      packName: pack.name,
      phase: 'pick',
      flow: 2,
      mapId,
      progress: { skipped: [], done: [], heardHook: false, listenedThrough: false },
    },
    bpm,
    totalBars: Math.min(BARS_RANGE.max, classSongMap(bpm, 2, mapId).length),
  };
}

const groupRank = (track) => Math.max(0, CLASS_GROUPS.indexOf(track.classSlot?.group));

export function buildMyBeat(arrangement) {
  const packTracks = arrangement.tracks.filter((track) => track.classSlot);
  const picked = packTracks.filter((track) => !track.muted)
    .sort((a, b) => groupRank(a) - groupRank(b) || a.classSlot.index - b.classSlot.index)
    .map((track) => ({ ...track, inBank: false }));
  const other = arrangement.tracks.filter((track) => !track.classSlot);
  const bank = packTracks.filter((track) => track.muted)
    .sort((a, b) => groupRank(a) - groupRank(b) || a.classSlot.index - b.classSlot.index)
    .map((track) => ({ ...track, muted: true, inBank: true }));
  return {
    ...arrangement,
    tracks: [...picked, ...other, ...bank],
    classSession: { ...arrangement.classSession, phase: 'arrange' },
  };
}

export function countTrackSlots(tracks = [], classSession = null) {
  const phase = classSession?.phase;
  return tracks.filter((track) => {
    if (track.inBank) return false;
    if (phase === 'pick' && track.classSlot && track.muted) return false;
    return true;
  }).length;
}

export const editableTracks = (tracks = []) => tracks.filter((track) => !track.inBank);

export function classTrackLimitNotice(phase) {
  return phase === 'arrange'
    ? `You can use up to ${MAX_TRACKS} tracks. Send one back to the Sound bank first.`
    : `You can use up to ${MAX_TRACKS} tracks. Mute one first.`;
}

function insertAtEndOfPickedGroup(tracks, promoted) {
  const without = tracks.filter((track) => track.id !== promoted.id);
  let insertAt = -1;
  without.forEach((track, index) => {
    if (!track.inBank && track.classSlot?.group === promoted.classSlot.group) insertAt = index;
  });
  if (insertAt < 0) {
    const rank = groupRank(promoted);
    without.forEach((track, index) => {
      if (!track.inBank && track.classSlot && groupRank(track) < rank) insertAt = index;
    });
  }
  const next = [...without];
  next.splice(insertAt + 1, 0, promoted);
  return next;
}

export function toggleClassTrackMute(arrangement, trackId, maxTracks = MAX_TRACKS) {
  const target = arrangement.tracks.find((track) => track.id === trackId);
  if (!target?.classSlot) return { arrangement, changed: false, blocked: false };
  const unmuting = target.muted;
  if (unmuting && countTrackSlots(arrangement.tracks, arrangement.classSession) >= maxTracks) {
    return { arrangement, changed: false, blocked: true, notice: classTrackLimitNotice(arrangement.classSession?.phase) };
  }
  const changed = { ...target, muted: !target.muted, inBank: unmuting ? false : target.inBank };
  const tracks = target.inBank && unmuting
    ? insertAtEndOfPickedGroup(arrangement.tracks, changed)
    : arrangement.tracks.map((track) => track.id === trackId ? changed : track);
  return { arrangement: { ...arrangement, tracks }, changed: true, blocked: false };
}

export function sendTrackToBank(arrangement, trackId) {
  const target = arrangement.tracks.find((track) => track.id === trackId);
  if (!target?.classSlot || target.inBank || arrangement.classSession?.phase !== 'arrange') {
    return { arrangement, changed: false };
  }
  const banked = { ...target, muted: true, inBank: true, solo: false };
  return {
    arrangement: {
      ...arrangement,
      tracks: [...arrangement.tracks.filter((track) => track.id !== trackId), banked],
    },
    changed: true,
  };
}

export function isEditableClip(tracks, clipId) {
  return editableTracks(tracks).some((track) => track.clips?.some((clip) => clip.id === clipId));
}
